import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { X, BriefcaseBusiness, ChevronLeft, ChevronRight } from "lucide-react";
import './CandidateJobMatchDetails.css';

type MatchBreakdown = {
  skill?: number;
  experience?: number;
  distance?: number;
  interest?: number;
};

type JobMatchItem = {
  job_position_id?: number;
  job_name?: string;
  match_percentage?: number;
  work_mode?: string;
  matched_skills?: string[];
  match_breakdown?: MatchBreakdown;
};

interface CandidateJobMatchDetailsProps {
  jobMatches?: JobMatchItem[] | null;
  primaryJobMatch?: JobMatchItem | null;
  fallbackJobName?: string;
  fallbackMatchPercentage?: number;
  candidateName?: string;
}

const getProgressColor = (percentage: number) =>
  percentage >= 75 ? "#22C55E" : percentage >= 50 ? "#F59E0B" : "#EF4444";

const getEligibilityLabel = (percentage: number) => {
  if (percentage >= 75) return "Strong";
  if (percentage >= 50) return "Moderate";
  return "Low Match";
};

const normalizePercentage = (value?: number) => {
  const pct = Number(value ?? 0);
  if (!Number.isFinite(pct)) return 0;
  return Math.max(0, Math.min(100, Math.round(pct)));
};

const buildJobMatches = ({
  jobMatches,
  primaryJobMatch,
  fallbackJobName,
  fallbackMatchPercentage,
}: CandidateJobMatchDetailsProps): JobMatchItem[] => {
  if (Array.isArray(jobMatches) && jobMatches.length) return jobMatches;
  if (primaryJobMatch?.job_name) return [primaryJobMatch];
  if (fallbackJobName) {
    return [
      {
        job_name: fallbackJobName,
        match_percentage: fallbackMatchPercentage ?? 0,
      },
    ];
  }
  return [];
};

// ─── Breakdown Card ──────────────────────────────────────────────────────────
const BreakdownCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  value: number;
}> = ({ icon, label, sublabel, value }) => {
  const color = getProgressColor(value);
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #E8EDF5",
      borderRadius: 14,
      padding: "14px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
          {icon} {label}
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color }}>{value}%</span>
      </div>
      <div style={{ height: 5, borderRadius: 99, background: "#E8EDF5", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 99, transition: "width 0.6s ease" }} />
      </div>
      <span style={{ fontSize: 11, color: "#94A3B8" }}>{sublabel}</span>
    </div>
  );
};

// ─── Full View Modal ──────────────────────────────────────────────────────────
const JobMatchFullModal: React.FC<{
  show: boolean;
  onHide: () => void;
  matches: JobMatchItem[];
  candidateName?: string;
}> = ({ show, onHide, matches, candidateName }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const match = matches[activeIndex];
  if (!match) return null;

  const percentage = normalizePercentage(match.match_percentage);
  const barColor = getProgressColor(percentage);
  const eligibilityLabel = getEligibilityLabel(percentage);
  const breakdown = match.match_breakdown;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="job-match-full-modal">
      <Modal.Body style={{ padding: 0, borderRadius: 20, overflow: "hidden" }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #F0F7FF 0%, #EEF4FF 100%)",
          padding: "24px 28px 20px",
          borderBottom: "1px solid #E8EDF5",
          position: "relative",
        }}>
          <button
            onClick={onHide}
            style={{
              position: "absolute", top: 16, right: 16,
              background: "#fff", border: "1px solid #E8EDF5",
              borderRadius: "50%", width: 32, height: 32,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#64748B",
            }}
          >
            <X size={16} />
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <BriefcaseBusiness size={18} color="#3B82F6" />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Job Match Details
            </span>
          </div>
          <h5 style={{ margin: 0, fontWeight: 700, color: "#0F172A", fontSize: 18 }}>
            How well this candidate fits your open roles
          </h5>
          {candidateName && (
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748B" }}>{candidateName}</p>
          )}

          {/* Job navigation */}
          {matches.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
              <button
                onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
                disabled={activeIndex === 0}
                style={{
                  width: 30, height: 30, borderRadius: "50%",
                  border: "1px solid #D1D5DB", background: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: activeIndex === 0 ? "not-allowed" : "pointer",
                  opacity: activeIndex === 0 ? 0.4 : 1,
                }}
              >
                <ChevronLeft size={14} />
              </button>
              <span style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>
                {activeIndex + 1} / {matches.length} job{matches.length > 1 ? "s" : ""}
              </span>
              <button
                onClick={() => setActiveIndex((i) => Math.min(matches.length - 1, i + 1))}
                disabled={activeIndex === matches.length - 1}
                style={{
                  width: 30, height: 30, borderRadius: "50%",
                  border: "1px solid #D1D5DB", background: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: activeIndex === matches.length - 1 ? "not-allowed" : "pointer",
                  opacity: activeIndex === matches.length - 1 ? 0.4 : 1,
                }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: "24px 28px", background: "#F8FAFC" }}>
          {/* Job title + overall score */}
          <div style={{
            background: "#fff",
            borderRadius: 16,
            padding: "20px 24px",
            marginBottom: 20,
            border: "1px solid #E8EDF5",
            boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 17, color: "#0F172A" }}>
                  {match.job_name || "Untitled Job"}
                </h6>
                {match.work_mode && (
                  <span style={{
                    display: "inline-block", marginTop: 4,
                    fontSize: 11, fontWeight: 600, color: "#3B82F6",
                    background: "#EFF6FF", borderRadius: 6, padding: "2px 8px",
                  }}>
                    {match.work_mode}
                  </span>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: barColor, lineHeight: 1 }}>{percentage}%</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: barColor, marginTop: 2 }}>{eligibilityLabel}</div>
              </div>
            </div>

            {/* Overall progress bar */}
            <div style={{ height: 10, borderRadius: 99, background: "#E8EDF5", overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${percentage}%`,
                background: `linear-gradient(90deg, ${barColor}cc, ${barColor})`,
                borderRadius: 99, transition: "width 0.8s ease",
              }} />
            </div>
          </div>

          {/* Breakdown grid */}
          {breakdown ? (
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                Match Breakdown
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <BreakdownCard
                  icon={<span>🎯</span>}
                  label="Skills Match"
                  sublabel="Skills vs job requirements"
                  value={normalizePercentage(breakdown.skill)}
                />
                <BreakdownCard
                  icon={<span>💼</span>}
                  label="Experience"
                  sublabel="Work experience alignment"
                  value={normalizePercentage(breakdown.experience)}
                />
                <BreakdownCard
                  icon={<span>📍</span>}
                  label="Location Fit"
                  sublabel="Proximity to job location"
                  value={normalizePercentage(breakdown.distance)}
                />
                <BreakdownCard
                  icon={<span>✨</span>}
                  label="Interest Fit"
                  sublabel="Candidate interests alignment"
                  value={normalizePercentage(breakdown.interest)}
                />
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <BreakdownCard icon={<span>🎯</span>} label="Skills Match" sublabel="Skills vs job requirements" value={percentage} />
              <BreakdownCard icon={<span>💼</span>} label="Experience" sublabel="Work experience alignment" value={Math.max(0, percentage - 5)} />
              <BreakdownCard icon={<span>📍</span>} label="Location Fit" sublabel="Proximity to job location" value={0} />
              <BreakdownCard icon={<span>✨</span>} label="Interest Fit" sublabel="Candidate interests alignment" value={0} />
            </div>
          )}

          {/* Matched skills */}
          {Array.isArray(match.matched_skills) && match.matched_skills.filter(Boolean).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                Matched Skills
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {match.matched_skills.filter(Boolean).map((skill) => (
                  <span key={skill} style={{
                    fontSize: 12, fontWeight: 500, color: "#3B82F6",
                    background: "#EFF6FF", border: "1px solid #BFDBFE",
                    borderRadius: 8, padding: "4px 10px",
                  }}>
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

// ─── Tab types ────────────────────────────────────────────────────────────────
type ActiveTab = "skills" | "experience" | "location" | "jobmatch" | "language" | null;

// ─── Tab detail content ───────────────────────────────────────────────────────
const TabDetailContent: React.FC<{
  tab: ActiveTab;
  match: JobMatchItem;
  percentage: number;
  barColor: string;
  eligibilityLabel: string;
}> = ({ tab, match, percentage, barColor, eligibilityLabel }) => {
  const breakdown = match.match_breakdown;

  if (tab === "skills") {
    const skills = Array.isArray(match.matched_skills) ? match.matched_skills.filter(Boolean) : [];
    return (
      <div style={{ padding: "10px 12px", display: "flex", flexWrap: "wrap", gap: 6 }}>
        {skills.length > 0 ? (
          <>
            {skills.slice(0, 6).map((skill) => (
              <span
                key={skill}
                style={{
                  fontSize: 12, fontWeight: 500, color: "#1E293B",
                  background: "#fff", border: "1px solid #9CA3AF",
                  borderRadius: 6, padding: "4px 12px", whiteSpace: "nowrap",
                }}
              >
                {skill}
              </span>
            ))}
            {skills.length > 6 && (
              <span style={{ fontSize: 11, color: "#94A3B8", alignSelf: "center" }}>
                +{skills.length - 6} more
              </span>
            )}
          </>
        ) : breakdown ? (
          [
            { label: "Skill", value: breakdown.skill },
            { label: "Exp", value: breakdown.experience },
            { label: "Distance", value: breakdown.distance },
            { label: "Interest", value: breakdown.interest },
          ].map(({ label, value }) => {
            const v = normalizePercentage(value);
            const c = getProgressColor(v);
            return (
              <span
                key={label}
                style={{
                  fontSize: 11, fontWeight: 500, color: c,
                  background: "#fff", border: `1px solid ${c}55`,
                  borderRadius: 6, padding: "3px 8px", whiteSpace: "nowrap",
                }}
              >
                {label} {v}%
              </span>
            );
          })
        ) : (
          <span style={{ fontSize: 12, color: "#94A3B8" }}>No skills data</span>
        )}
      </div>
    );
  }

  if (tab === "experience") {
    const expPct = normalizePercentage(breakdown?.experience);
    const expColor = getProgressColor(expPct);
    return (
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>💼 Experience Fit</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: expColor }}>{expPct}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 99, background: "#E8EDF5", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${expPct}%`, background: expColor, borderRadius: 99, transition: "width 0.6s ease" }} />
        </div>
        <span style={{ fontSize: 11, color: "#94A3B8" }}>Work experience alignment with job requirements</span>
      </div>
    );
  }

  if (tab === "location") {
    const locPct = normalizePercentage(breakdown?.distance);
    const locColor = getProgressColor(locPct);
    return (
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>📍 Location Fit</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: locColor }}>{locPct}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 99, background: "#E8EDF5", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${locPct}%`, background: locColor, borderRadius: 99, transition: "width 0.6s ease" }} />
        </div>
        <span style={{ fontSize: 11, color: "#94A3B8" }}>Proximity to job location</span>
      </div>
    );
  }

  if (tab === "jobmatch") {
    return (
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>🎯 Job Match</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: barColor }}>{percentage}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 99, background: "#D1E9FF", overflow: "hidden" }}>
          <div style={{
            height: "100%", width: `${percentage}%`,
            background: `linear-gradient(90deg, ${barColor}cc, ${barColor})`,
            borderRadius: 99, transition: "width 0.6s ease",
          }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 11, color: "#94A3B8" }}>Overall match for {match.job_name || "this job"}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: barColor }}>{eligibilityLabel}</span>
        </div>
      </div>
    );
  }

  if (tab === "language") {
    const interestPct = normalizePercentage(breakdown?.interest);
    const interestColor = getProgressColor(interestPct);
    return (
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>✨ Interest / Language Fit</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: interestColor }}>{interestPct}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 99, background: "#E8EDF5", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${interestPct}%`, background: interestColor, borderRadius: 99, transition: "width 0.6s ease" }} />
        </div>
        <span style={{ fontSize: 11, color: "#94A3B8" }}>Candidate interests & language alignment</span>
      </div>
    );
  }

  return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CandidateJobMatchDetails: React.FC<CandidateJobMatchDetailsProps> = (props) => {
  const [showFullModal, setShowFullModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<ActiveTab>(null);
  const matches = buildJobMatches(props);

  if (!matches.length) return null;

  const match = matches[activeIndex];
  const percentage = normalizePercentage(match.match_percentage);
  const barColor = getProgressColor(percentage);
  const eligibilityLabel = getEligibilityLabel(percentage);

  const tabs: { key: ActiveTab; label: string }[] = [
    { key: "skills", label: "Skills" },
    { key: "experience", label: "Experience" },
    { key: "location", label: "Location Fit" },
    { key: "jobmatch", label: "Job Match" },
    { key: "language", label: "Language" },
  ];

  const handleTabClick = (key: ActiveTab) => {
    setActiveTab((prev) => (prev === key ? null : key));
  };

  return (
    <>
      <div className="candidate-quickview__section">
        {/* Section header */}
        {/* <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h6 style={{ margin: 0 }}>Job Match Details</h6>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {matches.length > 1 && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: "#3B82F6",
                background: "#EFF6FF", borderRadius: 20, padding: "2px 10px",
              }}>
                {matches.length} job{matches.length > 1 ? "s" : ""}
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowFullModal(true)}
              style={{
                fontSize: 11, fontWeight: 600, color: "#3B82F6",
                background: "#EFF6FF", border: "1px solid #BFDBFE",
                borderRadius: 20, padding: "4px 12px", cursor: "pointer",
              }}
            >
              Full View
            </button>
          </div>
        </div> */}

        {/* Job navigation (inline) */}
        {matches.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <button
              onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
              disabled={activeIndex === 0}
              style={{
                width: 26, height: 26, borderRadius: "50%",
                border: "1px solid #D1D5DB", background: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: activeIndex === 0 ? "not-allowed" : "pointer",
                opacity: activeIndex === 0 ? 0.4 : 1,
              }}
            >
              <ChevronLeft size={12} />
            </button>
            <span style={{ fontSize: 11, color: "#64748B" }}>{activeIndex + 1} / {matches.length}</span>
            <button
              onClick={() => setActiveIndex((i) => Math.min(matches.length - 1, i + 1))}
              disabled={activeIndex === matches.length - 1}
              style={{
                width: 26, height: 26, borderRadius: "50%",
                border: "1px solid #D1D5DB", background: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: activeIndex === matches.length - 1 ? "not-allowed" : "pointer",
                opacity: activeIndex === matches.length - 1 ? 0.4 : 1,
              }}
            >
              <ChevronRight size={12} />
            </button>
          </div>
        )}

        {/* Compact job card */}
        <div
          className="candidate-quickview__job-card"
          style={{
            background: "#F0F7FF",
            border: "1px solid #D9ECFF",
            borderRadius: 16,
            padding: "16px 18px",
          }}
        >
          {/* Row 1: Job title + percentage */}
          <div
            style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 2, cursor: "pointer" }}
            onClick={() => setShowFullModal(true)}
          >
            <span style={{ fontSize: 15, fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
              {match.job_name || "Untitled Job"}
            </span>
            <span style={{ fontSize: 22, fontWeight: 800, color: barColor, lineHeight: 1, marginLeft: 12, whiteSpace: "nowrap" }}>
              {percentage}%
            </span>
          </div>

          {/* Row 2: Work mode + eligibility label */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>
              {match.work_mode || (activeIndex === 0 ? "Primary match" : "")}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: barColor }}>
              {eligibilityLabel}
            </span>
          </div>

          {/* Progress bar */}
          <div style={{ height: 8, borderRadius: 99, background: "#D1E9FF", overflow: "hidden", marginBottom: 14 }}>
            <div style={{
              height: "100%",
              width: `${percentage}%`,
              background: `linear-gradient(90deg, ${barColor}cc, ${barColor})`,
              borderRadius: 99,
              transition: "width 0.8s ease",
            }} />
          </div>

          {/* Tab table */}
          <div
            style={{
              border: "1px solid #D1D5DB",
              borderRadius: 10,
              overflow: "hidden",
              background: "#fff",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Column headers — all tabs */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
                borderBottom: activeTab ? "1px solid #E5E7EB" : "none",
              }}
            >
              {tabs.map((tab, idx) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => handleTabClick(tab.key)}
                    style={{
                      padding: "8px 6px",
                      fontSize: 12,
                      fontWeight: isActive ? 700 : 600,
                      color: isActive ? "#3B82F6" : "#1E293B",
                      background: isActive ? "#EFF6FF" : "transparent",
                      border: "none",
                      borderRight: idx < tabs.length - 1 ? "1px solid #E5E7EB" : "none",
                      cursor: "pointer",
                      textAlign: "center",
                      transition: "background 0.15s, color 0.15s",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isActive ? "Hide" : tab.label}
                  </button>
                );
              })}
            </div>

            {/* Detail content — shown only for active tab */}
            {activeTab && (
              <div style={{ borderTop: "1px solid #E5E7EB" }}>
                <TabDetailContent
                  tab={activeTab}
                  match={match}
                  percentage={percentage}
                  barColor={barColor}
                  eligibilityLabel={eligibilityLabel}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <JobMatchFullModal
        show={showFullModal}
        onHide={() => setShowFullModal(false)}
        matches={matches}
        candidateName={props.candidateName}
      />
    </>
  );
};

export default CandidateJobMatchDetails;
